import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  IconButton,
  Modal,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import { Login, Menu, Close } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import ScreenshotImage from "./ScreenshotImage";

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);

const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogin = () => {
    navigate("/tw/login");
  };

  const handleComecarGratis = () => {
    navigate("/tw/login?tab=cadastro");
  };

  const handleVerDemo = () => {
    const videoSection = document.getElementById("video-section");
    videoSection?.scrollIntoView({ behavior: "smooth" });
  };

  const navItems = [
    { label: "Recursos", href: "#features" },
    { label: "Preços", href: "#pricing" },
    { label: "Contato", href: "#footer" },
  ];

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    element?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #042E4B 0%, #0a4a6e 50%, #042E4B 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative Elements */}
      <Box
        sx={{
          position: "absolute",
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(239,68,60,0.15) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: -150,
          left: -150,
          width: 500,
          height: 500,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(239,68,60,0.1) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Header */}
      <Box
        component="header"
        sx={{
          px: { xs: 2, md: 8 },
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            component="img"
            src="/logo512teste.png"
            alt="Logo ThinkWay"
            sx={{ height: 40 }}
          />
          <Typography
            variant="h5"
            sx={{
              color: "white",
              fontWeight: 700,
              letterSpacing: "-0.5px",
            }}
          >
            ThinkWay
          </Typography>
        </Stack>

        {/* Desktop Navigation */}
        {!isMobile && (
          <Stack direction="row" alignItems="center" spacing={4}>
            {navItems.map((item) => (
              <Typography
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                sx={{
                  color: "rgba(255,255,255,0.85)",
                  cursor: "pointer",
                  fontWeight: 500,
                  transition: "color 0.2s",
                  "&:hover": {
                    color: "white",
                  },
                }}
              >
                {item.label}
              </Typography>
            ))}
            <Button
              variant="outlined"
              startIcon={<Login />}
              onClick={handleLogin}
              sx={{
                color: "white",
                borderColor: "rgba(255,255,255,0.5)",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Login
            </Button>
          </Stack>
        )}

        {/* Mobile Menu Button */}
        {isMobile && (
          <IconButton
            onClick={() => setMobileMenuOpen(true)}
            sx={{ color: "white" }}
          >
            <Menu />
          </IconButton>
        )}
      </Box>

      {/* Mobile Menu Modal */}
      <Modal open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            width: 280,
            bgcolor: "#042E4B",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" color="white">
              Menu
            </Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)} sx={{ color: "white" }}>
              <Close />
            </IconButton>
          </Stack>
          <Stack spacing={2}>
            {navItems.map((item) => (
              <Button
                key={item.label}
                fullWidth
                variant="text"
                onClick={() => scrollToSection(item.href)}
                sx={{ color: "white", justifyContent: "flex-start" }}
              >
                {item.label}
              </Button>
            ))}
            <Button
              fullWidth
              variant="contained"
              startIcon={<Login />}
              onClick={() => {
                handleLogin();
                setMobileMenuOpen(false);
              }}
            >
              Login
            </Button>
          </Stack>
        </Box>
      </Modal>

      {/* Hero Content */}
      <Container maxWidth="lg" sx={{ pt: { xs: 8, md: 12 }, pb: { xs: 6, md: 10 } }}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={{ xs: 6, lg: 8 }}
          alignItems="center"
        >
          {/* Text Content */}
          <Box sx={{ flex: 1, textAlign: { xs: "center", lg: "left" } }}>
            <MotionTypography
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              variant="h2"
              sx={{
                color: "white",
                fontWeight: 800,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem", lg: "3.5rem" },
                lineHeight: 1.2,
                mb: 3,
              }}
            >
              Planejamento Estratégico com{" "}
              <Box
                component="span"
                sx={{
                  color: "#EF443C",
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -4,
                    left: 0,
                    width: "100%",
                    height: 4,
                    backgroundColor: "#EF443C",
                    borderRadius: 2,
                  },
                }}
              >
                Inteligência Artificial
              </Box>
            </MotionTypography>

            <MotionTypography
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              variant="h6"
              sx={{
                color: "rgba(255,255,255,0.85)",
                fontWeight: 400,
                lineHeight: 1.7,
                mb: 4,
                maxWidth: 540,
                mx: { xs: "auto", lg: 0 },
              }}
            >
              Simplifique o desenvolvimento e a gestão do planejamento estratégico
              da sua empresa. Construa cenários, defina indicadores e alcance
              objetivos com uma plataforma intuitiva, acessível e potencializada por IA.
            </MotionTypography>

            <MotionBox
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent={{ xs: "center", lg: "flex-start" }}
              >
                <Button
                  size="large"
                  onClick={handleComecarGratis}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    background: "linear-gradient(135deg, #EF443C 0%, #ff6b5b 100%)",
                    boxShadow: "0 8px 24px rgba(239,68,60,0.4)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #d63d36 0%, #EF443C 100%)",
                      boxShadow: "0 12px 32px rgba(239,68,60,0.5)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  Começar Grátis
                </Button>
                <Button
                  size="large"
                  variant="outlined"
                  onClick={handleVerDemo}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    color: "white",
                    borderColor: "rgba(255,255,255,0.5)",
                    borderWidth: 2,
                    "&:hover": {
                      borderColor: "white",
                      backgroundColor: "rgba(255,255,255,0.1)",
                      borderWidth: 2,
                    },
                  }}
                >
                  Ver Demonstração
                </Button>
              </Stack>
            </MotionBox>

            {/* Trust Badges */}
            <MotionBox
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              sx={{ mt: 4 }}
            >
              <Stack
                direction="row"
                spacing={3}
                alignItems="center"
                justifyContent={{ xs: "center", lg: "flex-start" }}
                flexWrap="wrap"
                sx={{ gap: 2 }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "#4CAF50",
                    }}
                  />
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                    Teste grátis por 14 dias
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "#4CAF50",
                    }}
                  />
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                    Sem cartão de crédito
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: "#4CAF50",
                    }}
                  />
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }}>
                    Suporte em português
                  </Typography>
                </Stack>
              </Stack>
            </MotionBox>
          </Box>

          {/* Dashboard Image */}
          <MotionBox
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            sx={{
              flex: 1,
              width: "100%",
              maxWidth: { xs: "100%", lg: 600 },
            }}
          >
            <ScreenshotImage
              src="/screenshots/dashboard.png"
              alt="Dashboard ThinkWay"
              placeholderText="Screenshot do Dashboard"
              minHeight={{ xs: 300, md: 350 }}
              boxShadow="0 32px 64px rgba(0,0,0,0.4)"
              showOverlay
            />
          </MotionBox>
        </Stack>
      </Container>

      {/* Scroll Indicator */}
      <MotionBox
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        sx={{
          position: "absolute",
          bottom: 32,
          left: "50%",
          transform: "translateX(-50%)",
          display: { xs: "none", md: "block" },
        }}
      >
        <MotionBox
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <Box
            sx={{
              width: 30,
              height: 50,
              border: "2px solid rgba(255,255,255,0.3)",
              borderRadius: 15,
              display: "flex",
              justifyContent: "center",
              pt: 1,
            }}
          >
            <Box
              sx={{
                width: 4,
                height: 10,
                backgroundColor: "rgba(255,255,255,0.5)",
                borderRadius: 2,
              }}
            />
          </Box>
        </MotionBox>
      </MotionBox>
    </Box>
  );
};

export default HeroSection;
